import type { ReactElement, ReactNode } from 'react';
import React from 'react';
import type { TextStyle, TextProps, Text as RNText } from 'react-native';

import { TYPE_CONFIG } from '@garnish/constants';
import { useFluidTextStyles } from '../useFluidTextStyles';
import { Text } from '../Text';
import { webOnlyStyles } from '../../../utils';

export const BodyText = React.forwardRef(
  (props: BodyTextProps, ref: React.Ref<RNText>): ReactElement<Text> => {
    const {
      size = DEFAULT_SIZE,
      bold = false,
      style,
      children,
      ...rest
    } = props;

    const stylesFromPresets = getStylesFromPresets({ size, isBold: bold });
    const responsiveStyles = useFluidTextStyles(PRESET_MAP[size], 'BODY');
    const ligatureStyles = webOnlyStyles({ fontVariantLigatures: 'none' });

    return (
      <Text
        ref={ref}
        style={[stylesFromPresets, responsiveStyles, ligatureStyles, style]}
        {...rest}
      >
        {children}
      </Text>
    );
  },
);

/* -- utils, etc ------------------------------------------------------------ */

const PRESET_MAP = {
  1: '24',
  2: '18',
  3: '16',
  4: '14',
  5: '12',
};

const DEFAULT_SIZE: SizeOrdinal = 3;

const getStylesFromPresets = ({ size, isBold }: GetPresetProps): TextStyle => {
  const configKey = isBold ? `${PRESET_MAP[size]}_BOLD` : PRESET_MAP[size];

  return TYPE_CONFIG.BODY[configKey];
};

type GetPresetProps = { readonly size: SizeOrdinal; readonly isBold: boolean };

type SizeOrdinal = keyof typeof PRESET_MAP;

type BodyTextProps = {
  readonly size?: SizeOrdinal;
  readonly bold?: boolean;
  readonly underline?: boolean;
  readonly children: ReactNode;
} & TextProps;
